<div class="space-y-6">
    @let draft = agentTempData();
    <div class="flex items-start justify-between backdrop-blur-sm">
        <div>
            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Agent Configuration</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Define your agent's personality, behavior, and
                core settings</p>
        </div>
        <div>
            <app-button variant="default" [iconNameV2]="'save'"
                className="flex items-center justify-center gap-2 text-sm font-medium"
                [disabled]="saveDraftLoading()"
                (buttonClick)="onSaveDraft()">
                {{ saveDraftLoading() ? 'Saving…' : 'Save Draft' }}
            </app-button>
        </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6 backdrop-blur-sm items-stretch">
        <div class="flex flex-col gap-8">
            <div
                class="relative overflow-visible rounded-2xl border border-gray-200/90 dark:border-gray-700/70 bg-gradient-to-br from-white via-gray-50/80 to-violet-50/30 dark:from-gray-900/80 dark:via-gray-900/50 dark:to-violet-950/20 shadow-sm shadow-gray-900/5 dark:shadow-black/20">
                <div
                    class="pointer-events-none absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-violet-500 to-indigo-600 rounded-l-2xl">
                </div>
                <div class="relative pl-5 pr-4 py-4 sm:pl-6 sm:pr-5 sm:py-5">
                    <div class="flex gap-3 min-w-0">
                        <div
                            class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500/15 to-indigo-600/10 ring-1 ring-violet-500/20 dark:from-violet-400/10 dark:to-indigo-500/10 dark:ring-violet-400/15">
                            <app-icon-v2 name="layers" [size]="20" class="text-violet-600 dark:text-violet-300" />
                        </div>
                        <div class="min-w-0 space-y-1">
                            <div class="flex flex-wrap items-center gap-2">
                                <h3 class="text-sm font-semibold tracking-tight text-gray-900 dark:text-white">
                                    Default prompt base
                                </h3>
                                <span
                                    class="inline-flex items-center rounded-full bg-gray-100/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600 dark:bg-gray-800/80 dark:text-gray-400">
                                    Optional
                                </span>
                                <div class="relative flex items-center group/defaultprompt cursor-default">
                                    <app-icon-v2 name="info" [size]="13"
                                        class="text-gray-400 hover:text-violet-500 dark:text-gray-500 dark:hover:text-white transition-colors" />
                                    <div
                                        class="absolute left-0 sm:left-1/2 sm:-translate-x-1/2 bottom-full z-[60] mb-2 w-[min(100vw-2rem,22rem)] p-4 bg-gray-900 dark:bg-gray-950 rounded-xl border border-gray-700/80 text-left text-xs text-gray-100 shadow-xl shadow-black/25 opacity-0 invisible group-hover/defaultprompt:opacity-100 group-hover/defaultprompt:visible transition-all duration-200">
                                        <p class="font-semibold text-white mb-2">Default prompt behavior</p>
                                        <p class="text-gray-300 leading-relaxed mb-3">
                                            When you choose a default prompt, its text is inserted
                                            <span class="text-white font-medium">at the beginning</span> of your
                                            main system prompt at runtime. It establishes shared organizational
                                            guidelines (tone, safety, and role expectations) before your custom
                                            instructions below.
                                        </p>
                                        <ul class="space-y-1.5 text-gray-400 list-disc list-inside">
                                            <li>Applies across agents that use the same base template.</li>
                                            <li>Your textarea content still controls agent-specific behavior.</li>
                                            <li>Choose “No default prompt” to rely only on your system prompt.</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed max-w-prose">
                                Select an organization template to prepend standardized guidance, or leave unset for a
                                fully custom system prompt only.
                            </p>
                        </div>
                    </div>

                    <div class="mt-4 pb-1 relative z-[100]">
                        @if (promptsLoading()) {
                        <div
                            class="flex items-center gap-2 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-900/40 px-3 py-3 text-xs text-gray-500 dark:text-gray-400">
                            <span
                                class="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-violet-500 border-t-transparent"></span>
                            Loading default prompts…
                        </div>
                        } @else {
                        <app-dropdown [options]="defaultPromptDropdownOptions()"
                            [selectedValue]="defaultPromptDropdownSelectedValue()" selectionMode="single"
                            placeholder="Select a default prompt" [fullWidth]="true" variant="outline" size="md"
                            chevronIcon="chevronDown" [searchable]="defaultPromptDropdownSearchable()"
                            searchPlaceholder="Search prompts…"
                            className="w-full relative z-[100]"
                            [tooltipIsMarkdown]="true"
                            (selectionChange)="onDefaultPromptDropdownChange($event)" />
                        }
                    </div>

                    @if (selectedDefaultPromptPreview(); as preview) {
                    <div
                        class="mt-4 rounded-lg border border-violet-200/60 dark:border-violet-500/20 bg-violet-50/40 dark:bg-violet-950/25 px-3 py-2.5">
                        <p class="text-[10px] font-semibold uppercase tracking-wider text-violet-700 dark:text-violet-300/90 mb-1">
                            Preview · {{ preview.label || ('Prompt #' + preview.pid) }}
                        </p>
                        <div
                            class="text-xs text-gray-600 dark:text-gray-300 leading-relaxed font-mono break-words whitespace-pre-wrap rounded border border-violet-200/40 dark:border-violet-500/15 bg-white/40 dark:bg-black/15 px-2 py-1.5 min-h-0 pr-1"
                            [class.scrollbar-theme]="defaultPromptPreviewShowToggle()"
                            [class.overflow-y-auto]="defaultPromptPreviewShowToggle()"
                            [class.max-h-22]="defaultPromptPreviewShowToggle() && !defaultPromptPreviewExpanded()"
                            [class.max-h-72]="defaultPromptPreviewShowToggle() && defaultPromptPreviewExpanded()">
                            {{ preview.prompt }}
                        </div>
                        @if (defaultPromptPreviewShowToggle()) {
                        <button type="button" (click)="toggleDefaultPromptPreview()"
                            class="mt-2 text-xs font-medium text-violet-600 hover:text-violet-700 dark:text-violet-400 dark:hover:text-white underline-offset-2 hover:underline">
                            {{ defaultPromptPreviewExpanded() ? 'View less' : 'View more' }}
                        </button>
                        }
                    </div>
                    }
                </div>
            </div>

            <div class="flex flex-col gap-2 relative">
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-1">
                        <div class="flex items-center justify-center mb-0.5">
                            <app-icon-v2 name="asterisk" [size]="12" class="text-red-500" />
                        </div>
                        <p class="text-sm font-semibold text-gray-700 dark:text-white">System prompt</p>
                        <div class="relative flex items-center group/systemprompt cursor-pointer">
                            <app-icon-v2 name="info" [size]="12" class="text-gray-400 dark:text-white" />
                            <div
                                class="absolute left-1/2 -translate-x-1/2 -top-20 z-[110] w-64 p-3 bg-gray-800 dark:bg-gray-900 rounded-md border border-gray-700 text-xs text-gray-100 shadow-lg opacity-0 invisible group-hover/systemprompt:opacity-100 group-hover/systemprompt:visible transition-all duration-200">
                                <p>The system prompt is used to determine the persona of the agent and the context of
                                    the
                                    conversation. If a default prompt base is selected above, it is applied first, then
                                    this content.</p>
                            </div>
                        </div>
                    </div>
                    <button type="button"
                        (click)="openPromptGenerator()"
                        class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/10 hover:bg-violet-100 dark:hover:bg-white/10 border border-violet-100 dark:border-violet-700/50 transition-all duration-200 group">
                        <app-icon-v2 name="sparkles" [size]="14" class="text-violet-500 group-hover:animate-pulse" />
                        Generate AI Prompt
                    </button>
                </div>
                <div class="flex flex-col">
                    <textarea rows="15"
                        class="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-transparent px-3 pr-12 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-0 transition-shadow resize-none"
                        [value]="draft.systemPrompt"
                        (input)="patchAgentTemp({ systemPrompt: $any($event.target).value })"></textarea>
                </div>
            </div>

            <div class="flex flex-col gap-2">
                <div class="flex items-center justify-start gap-1">
                    <p class="text-sm font-semibold text-gray-700 dark:text-white">First Message</p>
                    <div class="relative flex items-center group/firstmsg cursor-pointer">
                        <app-icon-v2 name="infoCircle" [size]="12" class="text-gray-400 dark:text-white" />
                        <div
                            class="absolute left-1/2 -translate-x-1/2 -top-16 z-[110] w-64 p-3 bg-gray-800 dark:bg-gray-900 rounded-md border border-gray-700 text-xs text-gray-100 shadow-lg opacity-0 invisible group-hover/firstmsg:opacity-100 group-hover/firstmsg:visible transition-all duration-200">
                            <p>The first message the agent will say when the conversation starts.</p>
                        </div>
                    </div>
                </div>

                <textarea rows="4"
                    class="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-transparent px-3 pr-12 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-0 transition-shadow resize-none"
                    [value]="draft.firstMessage ?? ''"
                    (input)="patchAgentTemp({ firstMessage: $any($event.target).value || null })"
                    placeholder="The first message the agent will say when the conversation starts. If empty, the agent will wait for the user to start the conversation."></textarea>
            </div>
        </div>

        <div class="flex flex-col gap-0.5 h-full">
            <div
                class="rounded-xl border border-gray-200 dark:border-gray-700/60 bg-white dark:bg-gray-900/40 backdrop-blur-sm overflow-visible flex flex-col h-full">

                <div class="px-5 py-4 border-b border-gray-100 dark:border-gray-700/60">
                    <div class="flex items-center justify-between gap-3">
                        <div class="flex items-center gap-2 min-w-0">
                            <app-icon-v2 name="settings" [size]="18" class="text-gray-500 dark:text-gray-400 shrink-0" />
                            <h3 class="text-base font-semibold text-gray-800 dark:text-white truncate">Settings</h3>
                        </div>
                        <button type="button"
                            (click)="toggleExpandCollapseAllSettings()"
                            [attr.aria-label]="settingsPanelAllCollapsed() ? 'Expand all settings' : 'Collapse all settings'"
                            [title]="settingsPanelAllCollapsed() ? 'Expand all' : 'Collapse all'"
                            class="flex items-center justify-center shrink-0 p-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white/80 dark:bg-gray-800/60 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/80 hover:border-gray-300 dark:hover:border-gray-500 transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500/40">
                            <app-icon-v2 [name]="settingsExpandCollapseIcon()" [size]="18"
                                class="text-gray-600 dark:text-gray-300" />
                        </button>
                    </div>
                </div>

                <div class="divide-y divide-gray-100 dark:divide-gray-700/40 flex-1">

                    <div>
                        <button (click)="toggleSection('temperature')"
                            class="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                            <div class="flex items-center gap-1.5">
                                <app-icon-v2 name="thermometer" [size]="16" class="text-gray-800 dark:text-gray-200" />
                                <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Temperature</span>
                            </div>
                            <div class="flex items-center gap-2">
                                <span
                                    class="text-sm font-mono font-semibold text-gray-800 dark:text-white tabular-nums">{{
                                    draft.temperature | number:'1.2-2' }}</span>
                                <app-icon-v2 [name]="isSectionOpen('temperature') ? 'chevronUp' : 'chevronDown'"
                                    [size]="14" class="text-gray-400 dark:text-gray-500 transition-transform" />
                            </div>
                        </button>
                        @if (isSectionOpen('temperature')) {
                        <div class="px-5 pb-4 space-y-3">
                            <div class="flex items-center justify-end">
                                <span
                                    class="text-xs font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">{{
                                    temperatureLabel() }}</span>
                            </div>
                            <div class="relative group/temp">
                                <input type="range" min="0" max="2" step="0.01" [ngModel]="draft.temperature"
                                    (ngModelChange)="patchAgentTemp({ temperature: $event })"
                                    class="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-gradient-to-r from-gray-200 via-violet-300 to-violet-600 dark:from-gray-700 dark:via-violet-500 dark:to-violet-400 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:dark:bg-gray-900 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-violet-400 [&::-webkit-slider-thumb]:dark:border-violet-400 [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:dark:bg-gray-900 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-violet-400 [&::-moz-range-thumb]:shadow-md" />
                                <div class="flex justify-between mt-1">
                                    <span class="text-xs text-gray-400">0</span>
                                    <span class="text-xs text-gray-400">1</span>
                                    <span class="text-xs text-gray-400">2</span>
                                </div>
                            </div>
                        </div>
                        }
                    </div>

                    <div>
                        <button (click)="toggleSection('provider')"
                            class="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                            <div class="flex items-center gap-1.5">
                                <app-icon-v2 name="cpu" [size]="16" class="text-gray-800 dark:text-gray-200" />
                                <span class="text-sm font-medium text-gray-700 dark:text-gray-300">LLM Provider</span>
                            </div>
                            <div class="flex items-center gap-2 min-w-0">
                                <span class="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">{{
                                    providerLabel() }}</span>
                                <app-icon-v2 [name]="isSectionOpen('provider') ? 'chevronUp' : 'chevronDown'"
                                    [size]="14" class="text-gray-400 dark:text-gray-500 transition-transform" />
                            </div>
                        </button>
                        @if (isSectionOpen('provider')) {
                        <div class="px-5 pb-4 space-y-2">
                            <div class="grid grid-cols-2 gap-1.5 max-h-52 overflow-y-auto p-1 rounded-lg bg-gray-50 dark:bg-gray-800/60">
                                @for (p of providers(); track p.pid) {
                                <button type="button" (click)="onProviderSelect(p.pid)" [class]="
                                        draft.providerId === p.pid
                                            ? 'px-3 py-2 text-sm font-medium rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm border border-gray-200 dark:border-gray-600 transition-all text-left'
                                            : 'px-3 py-2 text-sm font-medium rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-all text-left'
                                    ">
                                    {{ p.name }}
                                </button>
                                }
                                @empty {
                                <p class="col-span-2 px-2 py-2 text-xs text-gray-500 dark:text-gray-400">No providers configured. Add providers in default settings.</p>
                                }
                            </div>
                        </div>
                        }
                    </div>

                    <div>
                        <button (click)="toggleSection('model')"
                            class="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                            <div class="flex items-center gap-1.5">
                                <app-icon-v2 name="box" [size]="16" class="text-gray-800 dark:text-gray-200" />
                                <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Model</span>
                            </div>
                            <div class="flex items-center gap-2">
                                <span class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ draft.llmModel
                                    }}</span>
                                <app-icon-v2 [name]="isSectionOpen('model') ? 'chevronUp' : 'chevronDown'" [size]="14"
                                    class="text-gray-400 dark:text-gray-500 transition-transform" />
                            </div>
                        </button>
                        @if (isSectionOpen('model')) {
                        <div class="px-5 pb-4 space-y-2 relative z-50 overflow-visible">
                            @if (modelsLoading() === draft.providerId) {
                            <p class="text-xs text-gray-500 dark:text-gray-400">Loading models…</p>
                            }
                            @if (draft.providerId == null) {
                            <p class="text-xs text-gray-500 dark:text-gray-400">Select an LLM provider first.</p>
                            } @else if (modelsForSelectedProvider().length === 0 && modelsLoading() !== draft.providerId) {
                            <p class="text-xs text-gray-500 dark:text-gray-400">No models for this provider.</p>
                            } @else {
                            <app-dropdown [options]="modelDropdownOptions()"
                                [selectedValue]="modelDropdownSelectedValue()" selectionMode="single"
                                placeholder="Select model" [fullWidth]="true" variant="outline" size="sm"
                                chevronIcon="chevronDown" className="w-full relative z-50"
                                (selectionChange)="onModelDropdownChange($event)" />
                            }
                        </div>
                        }
                    </div>

                    <div>
                        <button (click)="toggleSection('maxTokens')"
                            class="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                            <div class="flex items-center gap-1.5">
                                <app-icon-v2 name="hash" [size]="16" class="text-gray-800 dark:text-gray-200" />
                                <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Max Tokens</span>
                            </div>
                            <div class="flex items-center gap-2">
                                <span class="text-sm font-mono font-semibold text-gray-800 dark:text-white tabular-nums">{{ draft.maxTokens }}</span>
                                <app-icon-v2 [name]="isSectionOpen('maxTokens') ? 'chevronUp' : 'chevronDown'"
                                    [size]="14" class="text-gray-400 dark:text-gray-500 transition-transform" />
                            </div>
                        </button>
                        @if (isSectionOpen('maxTokens')) {
                        <div class="px-5 pb-4 space-y-2">
                            <div class="flex items-center justify-end">
                                <span class="text-xs text-gray-400 dark:text-gray-500">per flow</span>
                            </div>
                            <div class="relative">
                                <input type="number" [ngModel]="draft.maxTokens" (ngModelChange)="patchAgentTemp({ maxTokens: $event })"
                                    min="1" max="128000" step="256"
                                    class="w-full px-3 py-2 text-sm font-mono rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-400/40 transition-shadow [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                            </div>
                        </div>
                        }
                    </div>

                    <div>
                        <button (click)="toggleSection('budget')"
                            class="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                            <div class="flex items-center gap-1.5">
                                <app-icon-v2 name="wallet" [size]="16" class="text-gray-800 dark:text-gray-200" />
                                <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Weekly Budget</span>
                            </div>
                            <div class="flex items-center gap-2">
                                <span
                                    class="text-sm font-mono font-semibold text-gray-800 dark:text-white tabular-nums">{{
                                    formattedBudget() }} tokens</span>
                                <app-icon-v2 [name]="isSectionOpen('budget') ? 'chevronUp' : 'chevronDown'" [size]="14"
                                    class="text-gray-400 dark:text-gray-500 transition-transform" />
                            </div>
                        </button>
                        @if (isSectionOpen('budget')) {
                        <div class="px-5 pb-4">
                            <input type="range" min="10000" max="2000000" step="10000"
                                [ngModel]="draft.weeklyTokensBudget"
                                (ngModelChange)="patchAgentTemp({ weeklyTokensBudget: $event })"
                                class="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-gradient-to-r from-gray-200 via-violet-300 to-violet-600 dark:from-gray-700 dark:via-violet-500 dark:to-violet-400 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:dark:bg-gray-900 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-violet-400 [&::-webkit-slider-thumb]:dark:border-violet-400 [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:dark:bg-gray-900 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-violet-400 [&::-moz-range-thumb]:shadow-md" />
                            <div class="flex justify-between mt-1">
                                <span class="text-xs text-gray-400">10K</span>
                                <span class="text-xs text-gray-400">1M</span>
                                <span class="text-xs text-gray-400">2M</span>
                            </div>
                        </div>
                        }
                    </div>

                    <div>
                        <button (click)="toggleSection('timeout')"
                            class="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                            <div class="flex items-center gap-1.5">
                                <app-icon-v2 name="clock" [size]="16" class="text-gray-800 dark:text-gray-200" />
                                <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Timeout</span>
                            </div>
                            <div class="flex items-center gap-2">
                                <span
                                    class="text-sm font-mono font-semibold text-gray-800 dark:text-white tabular-nums">{{
                                    draft.llmTimeout }}s</span>
                                <app-icon-v2 [name]="isSectionOpen('timeout') ? 'chevronUp' : 'chevronDown'"
                                    [size]="14" class="text-gray-400 dark:text-gray-500 transition-transform" />
                            </div>
                        </button>
                        @if (isSectionOpen('timeout')) {
                        <div class="px-5 pb-4 space-y-2">
                            <div class="relative">
                                <input type="number" [ngModel]="draft.llmTimeout" (ngModelChange)="patchAgentTemp({ llmTimeout: $event })"
                                    min="5" max="300" step="5"
                                    class="w-full px-3 py-2 pr-10 text-sm font-mono rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-400/40 transition-shadow [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                                <span
                                    class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 dark:text-gray-500 pointer-events-none">sec</span>
                            </div>
                        </div>
                        }
                    </div>

                    <div>
                        <button (click)="toggleSection('stream')"
                            class="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                            <div class="flex items-center gap-1.5">
                                <app-icon-v2 name="radio" [size]="16" class="text-gray-800 dark:text-gray-200" />
                                <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Output Stream</span>
                            </div>
                            <div class="flex items-center gap-2">
                                <span class="text-xs font-medium"
                                    [class]="draft.outputStream ? 'text-emerald-500' : 'text-gray-400'">
                                    {{ draft.outputStream ? 'Enabled' : 'Disabled' }}
                                </span>
                                <app-icon-v2 [name]="isSectionOpen('stream') ? 'chevronUp' : 'chevronDown'" [size]="14"
                                    class="text-gray-400 dark:text-gray-500 transition-transform" />
                            </div>
                        </button>
                        @if (isSectionOpen('stream')) {
                        <div class="px-5 pb-4">
                            <div class="flex items-center justify-between">
                                <span class="text-xs text-gray-500 dark:text-gray-400">Toggle streaming
                                    output</span>
                                <app-toggle [checked]="draft.outputStream" [useIcons]="false"
                                    (toggleChange)="onStreamToggle($event)"></app-toggle>
                            </div>
                        </div>
                        }
                    </div>

                </div>
            </div>
        </div>
    </div>
</div>